import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import api, { getAssetUrl } from '../utils/api';
import { toast } from 'react-toastify';
import { User, Upload, MapPin, ChevronDown, CheckCircle, Settings, Sparkles, ExternalLink } from 'lucide-react';
import { FramedAvatar, StyledUsername, getAccentStyle } from '../components/cosmetics';

const REGINA_WARDS = [
  { id: 'Ward 1', name: 'Ward 1 - Arnheim Place / North Central' },
  { id: 'Ward 2', name: 'Ward 2 - Rosemont / Mount Royal' },
  { id: 'Ward 3', name: 'Ward 3 - Cathedral / Core / Downtown' },
  { id: 'Ward 4', name: 'Ward 4 - Dewdney East / Eastview' },
  { id: 'Ward 5', name: 'Ward 5 - Albert Park / Lakeview' },
  { id: 'Ward 6', name: 'Ward 6 - Normanview / Sherwood Estates' },
  { id: 'Ward 7', name: 'Ward 7 - Harbour Landing / Whitmore Park' },
  { id: 'Ward 8', name: 'Ward 8 - Hillsdale / Glencairn' },
  { id: 'Ward 9', name: 'Ward 9 - Wascana View / The Creeks' },
  { id: 'Ward 10', name: 'Ward 10 - Arcola East / Greens on Gardiner' },
];

const Account = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [profileForm, setProfileForm] = useState({ name: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [showWardSelector, setShowWardSelector] = useState(false);
  const [wardChangeLoading, setWardChangeLoading] = useState(false);
  const [userBadges, setUserBadges] = useState([]);
  const [bioForm, setBioForm] = useState({ bio: '', social_link: '' });
  const [bioSaving, setBioSaving] = useState(false);
  // Cosmetic toggles — track which ones are currently "on" locally
  const [cosmeticToggles, setCosmeticToggles] = useState({});
  const [cosmeticSaving, setCosmeticSaving] = useState(null);
  // Capture the ORIGINAL purchased values once on mount — never reset even when nulled in DB
  const cosmeticOriginals = useRef({});

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setProfileForm({ name: user.name || '' });
    setBioForm({ bio: user.bio || '', social_link: user.social_link || '' });
    setCosmeticToggles({
      accent_color: !!user.accent_color,
      name_style:   !!user.name_style,
      profile_frame: !!user.profile_frame,
    });
    // Always populate originals from current active values if present
    if (user.accent_color)  cosmeticOriginals.current.accent_color  = user.accent_color;
    if (user.name_style)    cosmeticOriginals.current.name_style    = user.name_style;
    if (user.profile_frame) cosmeticOriginals.current.profile_frame = user.profile_frame;

    // Also pull from purchase history to restore originals even after toggling off
    api.get('/store/my-redemptions').then(r => {
      const redemptions = r.data?.redemptions || [];
      const COLOUR_MAP = { 'Gold Accent': 'gold', 'Green Accent': 'green', 'Red Accent': 'red' };
      const STYLE_MAP  = { 'Bold Name': 'bold', 'Italic Name': 'italic' };
      const FRAME_MAP  = { 'Gold Glow Frame': 'gold_glow', 'Pulse Frame': 'pulse', 'Silver Frame': 'silver' };
      for (const r of redemptions) {
        const name = r.store_items?.name || '';
        if (COLOUR_MAP[name] && !cosmeticOriginals.current.accent_color)
          cosmeticOriginals.current.accent_color = COLOUR_MAP[name];
        if (STYLE_MAP[name] && !cosmeticOriginals.current.name_style)
          cosmeticOriginals.current.name_style = STYLE_MAP[name];
        if (FRAME_MAP[name] && !cosmeticOriginals.current.profile_frame)
          cosmeticOriginals.current.profile_frame = FRAME_MAP[name];
      }
    }).catch(() => {});

    api.get('/badges/my-badges')
      .then(r => setUserBadges(r.data.badges || []))
      .catch(() => {});
  }, [user, navigate]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    const trimmed = profileForm.name.trim();
    if (!trimmed) { toast.error('Name is required'); return; }
    setProfileSaving(true);
    try {
      const res = await api.put('/auth/profile', { name: trimmed });
      if (updateUser) updateUser({ ...user, ...res.data });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleBioSave = async () => {
    setBioSaving(true);
    try {
      await api.put('/auth/profile-sections', bioForm);
      if (updateUser) updateUser({ ...user, ...bioForm });
      toast.success('Profile section saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setBioSaving(false);
    }
  };

  const handleCosmeticToggle = async (field) => {
    const currentlyOn = cosmeticToggles[field];
    // When re-enabling, restore from captured originals — user[field] may already be null
    const originalValue = cosmeticOriginals.current[field];
    const newValue = currentlyOn ? null : (originalValue || null);

    if (!currentlyOn && !originalValue) {
      toast.info('Visit the Store to reactivate this cosmetic.');
      return;
    }
    setCosmeticSaving(field);
    try {
      await api.put('/auth/cosmetics/toggle', { field, value: newValue });
      setCosmeticToggles(prev => ({ ...prev, [field]: !currentlyOn }));
      if (updateUser) updateUser({ ...user, [field]: newValue });
      toast.success(currentlyOn ? `${field.replace(/_/g, ' ')} hidden` : `${field.replace(/_/g, ' ')} restored`);
    } catch (err) {
      toast.error('Could not update cosmetic');
    } finally {
      setCosmeticSaving(null);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/auth/upload-profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (updateUser) updateUser({ ...user, profile_image_url: res.data.image_url });
      toast.success('Profile picture updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    }
  };

  const handleWardChange = async (ward) => {
    setWardChangeLoading(true);
    try {
      await api.put('/auth/change-ward', { ward });
      if (updateUser) updateUser({ ...user, ward });
      toast.success(`Ward updated to ${ward}!`);
      setShowWardSelector(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update ward');
    } finally {
      setWardChangeLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-10">

        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-headline text-3xl text-white">ACCOUNT</h1>
            <p className="font-mono text-xs text-gray-500 mt-1 uppercase tracking-wider">Profile &amp; Settings</p>
          </div>
          <Link
            to="/dashboard"
            className="font-mono text-xs text-gray-400 hover:text-white border border-[#2a2a2a] px-3 py-2 hover:border-[#C5A52A]/50 transition-colors"
          >
            ← BACK TO DASHBOARD
          </Link>
        </div>

        {/* Avatar + Name Section */}
        <div className="bg-[#111] border border-[#1a1a1a] rounded p-6 mb-4">
          <h2 className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-4">Profile Photo</h2>
          <div className="flex items-center gap-6">
            <div className="relative group">
              {user.profile_image_url ? (
                <img
                  src={getAssetUrl(user.profile_image_url)}
                  alt={user.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 bg-[#1a1a1a] rounded-full flex items-center justify-center">
                  <User className="w-10 h-10 text-gray-500" />
                </div>
              )}
              <label
                htmlFor="account-photo-upload"
                className="absolute inset-0 bg-black/70 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <Upload className="w-6 h-6 text-white" />
              </label>
              <input
                id="account-photo-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePictureUpload}
              />
            </div>
            <div>
              <p className="text-white font-display text-lg">{user.username?.toUpperCase() || user.name?.toUpperCase()}</p>
              <p className="font-mono text-xs text-gray-500">{user.email}</p>
              <div className="flex items-center gap-2 mt-2">
                {user.is_verified && (
                  <span className="flex items-center gap-1 bg-green-900/30 border border-green-800/50 rounded px-2 py-0.5">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span className="font-mono text-[10px] text-green-500">VERIFIED</span>
                  </span>
                )}
                <Link
                  to={`/profile/${user.id}`}
                  className="font-mono text-[10px] text-[#C5A52A] border border-[#C5A52A]/40 px-2 py-0.5 hover:border-[#C5A52A] transition-colors"
                >
                  VIEW PUBLIC PROFILE →
                </Link>
              </div>
              <label
                htmlFor="account-photo-upload"
                className="mt-3 inline-flex items-center gap-2 text-[10px] font-mono text-gray-400 border border-[#2a2a2a] px-3 py-1.5 cursor-pointer hover:text-white hover:border-[#C5A52A]/40 transition-colors"
              >
                <Upload className="w-3 h-3" />
                CHANGE PHOTO
              </label>
            </div>
          </div>
        </div>

        {/* Display Name */}
        <div className="bg-[#111] border border-[#1a1a1a] rounded p-6 mb-4">
          <h2 className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-4">Display Name</h2>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono text-xs text-[#C5A52A] mb-2 uppercase tracking-wider">Display Name</label>
                <input
                  type="text"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ name: e.target.value })}
                  className="w-full px-4 py-3 bg-black border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors"
                  placeholder="Your display name"
                />
              </div>
              <div>
                <label className="block font-mono text-xs text-gray-600 mb-2 uppercase tracking-wider">Username (locked)</label>
                <div className="w-full px-4 py-3 bg-black border border-[#1a1a1a] text-gray-600 font-mono text-sm">
                  {user.username}
                </div>
                <p className="mt-1 text-[10px] font-mono text-gray-600">Contact admin to change username</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={profileSaving}
                className="bg-[#C5A52A] hover:bg-[#D4B93A] disabled:opacity-50 text-black font-display text-sm px-6 py-2 tracking-wider transition-colors"
              >
                {profileSaving ? 'SAVING...' : 'SAVE CHANGES'}
              </button>
            </div>
          </form>
        </div>

        {/* Ward */}
        <div className="bg-[#111] border border-[#1a1a1a] rounded p-6 mb-4">
          <h2 className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-4">Ward Assignment</h2>
          <div className="flex items-center justify-between">
            <div>
              {user.ward ? (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-400" />
                  <span className="font-display text-white">{user.ward}</span>
                  <span className="font-mono text-xs text-gray-500">— {REGINA_WARDS.find(w => w.id === user.ward)?.name.split(' - ')[1]}</span>
                </div>
              ) : (
                <p className="font-mono text-xs text-gray-500">No ward selected</p>
              )}
              <p className="font-mono text-[10px] text-gray-600 mt-1">Ward changes limited to once every 90 days</p>
            </div>
            <button
              onClick={() => setShowWardSelector(!showWardSelector)}
              className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#2a2a2a] border border-[#2a2a2a] text-gray-300 font-mono text-xs px-3 py-2 transition-colors"
            >
              <MapPin className="w-3.5 h-3.5" />
              {user.ward ? 'CHANGE WARD' : 'SET WARD'}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
          {showWardSelector && (
            <div className="mt-3 border border-[#2a2a2a] rounded bg-[#0a0a0a]">
              <div className="max-h-52 overflow-y-auto">
                {REGINA_WARDS.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => handleWardChange(w.id)}
                    disabled={wardChangeLoading || w.id === user.ward}
                    className={`w-full text-left px-4 py-2.5 text-xs font-mono transition-colors ${
                      w.id === user.ward
                        ? 'text-[#C5A52A] bg-[#C5A52A]/10 cursor-default'
                        : 'text-gray-300 hover:bg-[#1a1a1a] hover:text-white'
                    }`}
                  >
                    {w.id === user.ward ? `✓ ${w.name}` : w.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Badges */}
        {userBadges.length > 0 && (
          <div className="bg-[#111] border border-[#1a1a1a] rounded p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-mono text-xs text-gray-500 uppercase tracking-wider">Your Badges</h2>
              <Link to="/badges" className="font-mono text-xs text-[#C5A52A] hover:underline">View All →</Link>
            </div>
            <div className="flex flex-wrap gap-2">
              {userBadges.map((badge, i) => (
                <div key={badge.badge_slug || badge.badge_name || `badge-${i}`} className="flex items-center gap-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2">
                  {badge.badge_icon_url?.startsWith('http') ? (
                    <img src={badge.badge_icon_url} alt={badge.badge_name} className="w-5 h-5" />
                  ) : (
                    <span className="text-lg">{badge.badge_icon_url || '🏅'}</span>
                  )}
                  <span className="font-mono text-xs text-gray-300">{badge.badge_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cosmetics Preview */}
        {(cosmeticOriginals.current.accent_color || cosmeticOriginals.current.name_style || cosmeticOriginals.current.profile_frame ||
          user.accent_color || user.name_style || user.profile_frame) && (
          <div className="bg-[#111] border border-[#C5A52A]/20 rounded p-6 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#C5A52A]" />
                <h2 className="font-mono text-xs text-[#C5A52A] uppercase tracking-wider">Active Cosmetics</h2>
              </div>
              <Link to="/store" className="font-mono text-xs text-gray-500 hover:text-[#C5A52A]">Get More →</Link>
            </div>

            {/* Live preview — updates as toggles change */}
            <div className="flex items-center gap-5 mb-5 pb-5 border-b border-[#1a1a1a]">
              <FramedAvatar
                user={cosmeticToggles.profile_frame ? user : { ...user, profile_frame: null }}
                size={14}
              />
              <div>
                <StyledUsername
                  user={cosmeticToggles.accent_color || cosmeticToggles.name_style ? user : { ...user, accent_color: null, name_style: null }}
                  className="text-lg"
                >
                  {user.name || user.username}
                </StyledUsername>
                <p className="text-gray-700 font-mono text-[10px] mt-0.5">Preview of how you appear on posts</p>
              </div>
            </div>

            {/* Per-cosmetic rows — show ALL owned cosmetics, on or off */}
            <div className="space-y-2">
              {[
                { field: 'accent_color',  label: 'Username Accent Colour' },
                { field: 'name_style',    label: 'Username Style' },
                { field: 'profile_frame', label: 'Profile Frame' },
              ].filter(c => cosmeticOriginals.current[c.field] || user[c.field]).map(({ field, label }) => {
                const value = cosmeticOriginals.current[field] || user[field];
                const isOn = cosmeticToggles[field];
                return (
                  <div key={field} className={`flex items-center justify-between px-3 py-2 border transition-colors ${isOn ? 'bg-[#0D0D0D] border-[#1E1E1E]' : 'bg-[#0a0a0a] border-[#151515] opacity-60'}`}>
                    <div>
                      <span className={`font-mono text-xs ${isOn ? 'text-gray-300' : 'text-gray-600'}`}>{label}</span>
                      <span className="font-mono text-[10px] text-gray-600 ml-2">
                        {field === 'accent_color' && <span style={getAccentStyle(value)}>● </span>}
                        {value.replace(/_/g, ' ')}
                      </span>
                      {!isOn && <span className="font-mono text-[10px] text-gray-700 ml-2">(hidden)</span>}
                    </div>
                    <button
                      onClick={() => handleCosmeticToggle(field)}
                      disabled={cosmeticSaving === field}
                      className={`font-mono text-[10px] uppercase tracking-wider px-3 py-1 border transition-colors ${
                        isOn
                          ? 'border-red-900/60 text-red-400 hover:border-red-600 hover:text-red-300'
                          : 'border-green-900/60 text-green-500 hover:border-green-500'
                      }`}
                    >
                      {cosmeticSaving === field ? '...' : isOn ? 'Hide' : 'Show'}
                    </button>
                  </div>
                );
              })}
            </div>
            <p className="text-gray-700 font-mono text-[10px] mt-3">
              Hiding a cosmetic removes it from your posts. You can re-enable it anytime — you still own it.
            </p>
          </div>
        )}

        {/* Unlocked Profile Sections */}
        {(user.unlocked_sections || []).length > 0 && (
          <div className="bg-[#111] border border-[#1a1a1a] rounded p-6 mb-4">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#C5A52A]" />
              <h2 className="font-mono text-xs text-[#C5A52A] uppercase tracking-wider">Unlocked Profile Sections</h2>
            </div>
            <div className="space-y-4">
              {(user.unlocked_sections || []).includes('extended_bio') && (
                <div>
                  <label className="block font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">
                    Bio <span className="text-gray-700">(max 400 characters · visible on public profile)</span>
                  </label>
                  <textarea
                    value={bioForm.bio}
                    onChange={e => setBioForm({ ...bioForm, bio: e.target.value.slice(0, 400) })}
                    rows={3}
                    placeholder="Tell your ward who you are..."
                    className="w-full px-4 py-3 bg-black border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors resize-none"
                  />
                  <p className="text-right font-mono text-[10px] text-gray-700 mt-1">{bioForm.bio.length}/400</p>
                </div>
              )}
              {(user.unlocked_sections || []).includes('social_link') && (
                <div>
                  <label className="block font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">
                    Social / Website Link <span className="text-gray-700">(visible on public profile)</span>
                  </label>
                  <div className="relative">
                    <ExternalLink className="absolute left-3 top-3.5 w-4 h-4 text-gray-600" />
                    <input
                      type="text"
                      value={bioForm.social_link}
                      onChange={e => setBioForm({ ...bioForm, social_link: e.target.value })}
                      placeholder="https://yourwebsite.ca"
                      className="w-full pl-10 pr-4 py-3 bg-black border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleBioSave}
                  disabled={bioSaving}
                  className="bg-[#C5A52A] hover:bg-[#D4B93A] disabled:opacity-50 text-black font-display text-sm px-6 py-2 tracking-wider transition-colors"
                >
                  {bioSaving ? 'SAVING...' : 'SAVE PROFILE SECTIONS'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Store CTA if no cosmetics yet */}
        {!(user.accent_color || user.name_style || user.profile_frame) && (user.unlocked_sections || []).length === 0 && (
          <div className="bg-[#0a0a0a] border border-dashed border-[#2a2a2a] rounded p-6 mb-4 text-center">
            <Sparkles className="w-8 h-8 text-gray-700 mx-auto mb-3" />
            <p className="font-mono text-xs text-gray-600 mb-3">Earn points by posting, commenting, and reporting issues — then spend them here.</p>
            <Link
              to="/store"
              className="inline-flex items-center gap-2 font-mono text-xs text-[#C5A52A] border border-[#C5A52A]/30 px-4 py-2 hover:border-[#C5A52A] transition-colors"
            >
              <Sparkles className="w-3 h-3" />
              VISIT THE STORE
            </Link>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default Account;
